import React, { useMemo, useState } from "react";
import {
  useGetEnglishBlueprintAvailabilityQuery,
} from "../../slices/englishQuizBlueprintApiSlice";

import BlueprintAvailabilityTable from "./components/BlueprintAvailabilityTable";

function getErrorMessage(error) {
  if (!error) {
    return "";
  }

  if (typeof error === "string") {
    return error;
  }

  return (
    error?.data?.message ||
    error?.data?.error ||
    error?.message ||
    "Unable to load blueprint availability."
  );
}

function getAvailability(response) {
  if (!response) {
    return null;
  }

  return (
    response?.data?.availability ||
    response?.data ||
    response?.availability ||
    response
  );
}

function getRows(data) {
  if (!data) {
    return [];
  }

  if (Array.isArray(data)) {
    return data;
  }

  if (Array.isArray(data.availability)) {
    return data.availability;
  }

  if (Array.isArray(data.sections)) {
    return data.sections;
  }

  if (Array.isArray(data.results)) {
    return data.results;
  }

  if (Array.isArray(data.items)) {
    return data.items;
  }

  return [];
}

function getBooleanValue(value) {
  if (typeof value === "boolean") {
    return value;
  }

  if (
    value === "true" ||
    value === 1 ||
    value === "1"
  ) {
    return true;
  }

  return false;
}

function getStatus(item) {
  if (
    item?.available === true ||
    item?.isAvailable === true ||
    item?.sufficient === true ||
    item?.valid === true
  ) {
    return "available";
  }

  if (
    item?.available === false ||
    item?.isAvailable === false ||
    item?.sufficient === false ||
    item?.valid === false
  ) {
    return "insufficient";
  }

  const status = String(
    item?.status || ""
  ).toLowerCase();

  if (
    status === "available" ||
    status === "sufficient" ||
    status === "ok" ||
    status === "valid"
  ) {
    return "available";
  }

  if (
    status === "unavailable" ||
    status === "insufficient" ||
    status === "invalid" ||
    status === "missing"
  ) {
    return "insufficient";
  }

  return "unknown";
}

function StatusBadge({ status }) {
  const styles = {
    available:
      "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
    insufficient:
      "bg-red-50 text-red-700 ring-red-600/20",
    unknown:
      "bg-gray-100 text-gray-600 ring-gray-500/20",
  };

  const labels = {
    available: "Available",
    insufficient: "Insufficient",
    unknown: "Unknown",
  };

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide ring-1 ring-inset ${
        styles[status] || styles.unknown
      }`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${
          status === "available"
            ? "bg-emerald-500"
            : status === "insufficient"
              ? "bg-red-500"
              : "bg-gray-400"
        }`}
      />
      {labels[status] || "Unknown"}
    </span>
  );
}

function SummaryCard({
  label,
  value,
  description,
  color,
}) {
  const colors = {
    indigo:
      "border-indigo-100 bg-indigo-50 text-indigo-700",
    emerald:
      "border-emerald-100 bg-emerald-50 text-emerald-700",
    red:
      "border-red-100 bg-red-50 text-red-700",
    amber:
      "border-amber-100 bg-amber-50 text-amber-700",
  };

  return (
    <div
      className={`rounded-xl border p-4 ${
        colors[color] || colors.indigo
      }`}
    >
      <p className="text-[10px] font-semibold uppercase tracking-wide opacity-70">
        {label}
      </p>

      <p className="mt-1 text-2xl font-bold">
        {value}
      </p>

      {description && (
        <p className="mt-1 text-[10px] opacity-70">
          {description}
        </p>
      )}
    </div>
  );
}

function LoadingState() {
  return (
    <div className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map(
          (item) => (
            <div
              key={item}
              className="h-24 animate-pulse rounded-xl bg-gray-100"
            />
          )
        )}
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-200">
        <div className="h-12 animate-pulse bg-gray-100" />

        <div className="space-y-px bg-gray-100">
          {[1, 2, 3, 4, 5].map(
            (item) => (
              <div
                key={item}
                className="h-16 animate-pulse bg-white"
              />
            )
          )}
        </div>
      </div>
    </div>
  );
}

export default function EnglishQuizBlueprintAvailability({
  blueprintId,
  quizId,
  blueprint = null,
  autoFetch = true,
  onRefresh,
  onClose,
  onCreateQuestions,
  className = "",
}) {
  const [showOnlyIssues, setShowOnlyIssues] =
    useState(false);

  const queryArgument = useMemo(() => {
    if (blueprintId) {
      return blueprintId;
    }

    return quizId;
  }, [blueprintId, quizId]);

  const skip =
    !autoFetch ||
    !queryArgument;

  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch,
  } =
    useGetEnglishBlueprintAvailabilityQuery(
      queryArgument,
      {
        skip,
      }
    );

  const availability =
    getAvailability(data);

  const rows = useMemo(
    () => getRows(availability),
    [availability]
  );

  const normalizedRows =
    useMemo(
      () =>
        rows.map((item, index) => {
          const requested =
            Number(
              item?.required ??
                item?.requiredCount ??
                item?.selectionCount ??
                item?.requested ??
                item?.count ??
                0
            );

          const available =
            Number(
              item?.availableCount ??
                item?.availableQuestions ??
                item?.available ??
                item?.totalAvailable ??
                0
            );

          const shortage =
            Number(
              item?.shortage ??
                item?.missing ??
                Math.max(
                  requested - available,
                  0
                )
            );

          return {
            ...item,
            id:
              item?.id ||
              item?._id ||
              `availability-${index}`,
            sectionName:
              item?.sectionName ||
              item?.name ||
              item?.section?.name ||
              `Section ${index + 1}`,
            requested,
            available,
            shortage,
            status:
              item?.status ||
              getStatus(item),
          };
        }),
      [rows]
    );

  const visibleRows =
    useMemo(
      () =>
        showOnlyIssues
          ? normalizedRows.filter(
              (item) =>
                item.status !==
                  "available" ||
                item.shortage > 0
            )
          : normalizedRows,
      [
        normalizedRows,
        showOnlyIssues,
      ]
    );

  const summary = useMemo(() => {
    const available = normalizedRows.filter(
      (item) =>
        item.status === "available" &&
        item.shortage <= 0
    ).length;

    const insufficient =
      normalizedRows.filter(
        (item) =>
          item.status !==
            "available" ||
          item.shortage > 0
      ).length;

    const requested =
      normalizedRows.reduce(
        (total, item) =>
          total + item.requested,
        0
      );

    const availableQuestions =
      normalizedRows.reduce(
        (total, item) =>
          total + item.available,
        0
      );

    const shortage =
      normalizedRows.reduce(
        (total, item) =>
          total + item.shortage,
        0
      );

    return {
      sections:
        normalizedRows.length,
      available,
      insufficient,
      requested,
      availableQuestions,
      shortage,
    };
  }, [normalizedRows]);

  const isReady =
    summary.insufficient === 0 &&
    normalizedRows.length > 0;

  const handleRefresh =
    async () => {
      if (onRefresh) {
        await onRefresh();
        return;
      }

      if (refetch) {
        await refetch();
      }
    };

  if (!queryArgument) {
    return (
      <div
        className={`rounded-xl border border-amber-200 bg-amber-50 p-5 ${className}`}
      >
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-100 text-amber-600">
            <svg
              viewBox="0 0 20 20"
              fill="currentColor"
              className="h-4 w-4"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.721-1.36 3.486 0l6.58 11.69c.75 1.334-.213 2.985-1.743 2.985H3.42c-1.53 0-2.493-1.651-1.743-2.985l6.58-11.69zM10 7a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 7zm0 7.25a1 1 0 100-2 1 1 0 000 2z"
                clipRule="evenodd"
              />
            </svg>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-amber-900">
              Blueprint ID is required
            </h3>

            <p className="mt-1 text-xs leading-5 text-amber-700">
              Provide a blueprint ID or quiz ID to
              check question availability.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading || isFetching) {
    return (
      <div
        className={`w-full space-y-5 ${className}`}
      >
        <div className="rounded-2xl border border-gray-200 bg-white p-5">
          <div className="h-5 w-64 animate-pulse rounded bg-gray-200" />
          <div className="mt-2 h-3 w-96 max-w-full animate-pulse rounded bg-gray-100" />
        </div>

        <LoadingState />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`rounded-xl border border-red-200 bg-red-50 p-5 ${className}`}
      >
        <div className="flex items-start gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-red-100 text-red-600">
            <svg
              viewBox="0 0 20 20"
              fill="currentColor"
              className="h-4 w-4"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.721-1.36 3.486 0l6.58 11.69c.75 1.334-.213 2.985-1.743 2.985H3.42c-1.53 0-2.493-1.651-1.743-2.985l6.58-11.69zM10 7a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 7zm0 7.25a1 1 0 100-2 1 1 0 000 2z"
                clipRule="evenodd"
              />
            </svg>
          </div>

          <div className="flex-1">
            <h3 className="text-sm font-semibold text-red-900">
              Unable to check availability
            </h3>

            <p className="mt-1 text-xs leading-5 text-red-700">
              {getErrorMessage(error)}
            </p>

            <button
              type="button"
              onClick={handleRefresh}
              className="mt-3 inline-flex items-center gap-2 rounded-lg bg-red-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-red-700"
            >
              <svg
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                className="h-4 w-4"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16.5 9.5a6.5 6.5 0 10-1.9 4.6"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16.5 5.5v4h-4"
                />
              </svg>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`w-full space-y-5 ${className}`}
    >
      <div className="rounded-2xl border border-gray-200 bg-white">
        <div className="flex flex-col gap-4 border-b border-gray-200 px-5 py-5 sm:flex-row sm:items-start sm:justify-between sm:px-6">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-lg font-semibold text-gray-900">
                Blueprint Availability
              </h1>

              <span
                className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide ring-1 ring-inset ${
                  isReady
                    ? "bg-emerald-50 text-emerald-700 ring-emerald-600/20"
                    : "bg-red-50 text-red-700 ring-red-600/20"
                }`}
              >
                {isReady
                  ? "Ready"
                  : "Issues Found"}
              </span>
            </div>

            <p className="mt-1 text-sm leading-6 text-gray-500">
              Check whether the question bank has
              enough English questions to satisfy every
              blueprint section.
            </p>

            {blueprint && (
              <p className="mt-2 text-xs font-medium text-gray-700">
                {blueprint.name ||
                  blueprint.title ||
                  "English Quiz Blueprint"}
              </p>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 transition hover:bg-gray-50"
              >
                Close
              </button>
            )}

            <button
              type="button"
              onClick={handleRefresh}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 transition hover:bg-gray-50"
            >
              <svg
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                className="h-4 w-4"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16.5 9.5a6.5 6.5 0 10-1.9 4.6"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M16.5 5.5v4h-4"
                />
              </svg>
              Refresh
            </button>
          </div>
        </div>

        <div className="space-y-5 p-5 sm:p-6">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <SummaryCard
              label="Sections"
              value={summary.sections}
              description="Blueprint sections checked"
              color="indigo"
            />

            <SummaryCard
              label="Available"
              value={summary.available}
              description="Sections with enough questions"
              color="emerald"
            />

            <SummaryCard
              label="Issues"
              value={summary.insufficient}
              description="Sections requiring attention"
              color="red"
            />

            <SummaryCard
              label="Question Shortage"
              value={summary.shortage}
              description={`${summary.availableQuestions} available of ${summary.requested} requested`}
              color="amber"
            />
          </div>

          {availability &&
            !Array.isArray(
              availability
            ) && (
              <div className="grid gap-3 rounded-xl border border-gray-200 bg-gray-50 p-4 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                    Blueprint
                  </p>

                  <p className="mt-1 truncate text-xs font-medium text-gray-700">
                    {availability.blueprintName ||
                      blueprint?.name ||
                      blueprint?.title ||
                      "English Blueprint"}
                  </p>
                </div>

                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                    Required
                  </p>

                  <p className="mt-1 text-xs font-medium text-gray-700">
                    {availability.totalRequired ??
                      summary.requested}
                  </p>
                </div>

                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                    Available
                  </p>

                  <p className="mt-1 text-xs font-medium text-gray-700">
                    {availability.totalAvailable ??
                      summary.availableQuestions}
                  </p>
                </div>

                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                    Overall Status
                  </p>

                  <div className="mt-1">
                    <StatusBadge
                      status={
                        getStatus(
                          availability
                        ) ||
                        (isReady
                          ? "available"
                          : "insufficient")
                      }
                    />
                  </div>
                </div>
              </div>
            )}

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-sm font-semibold text-gray-900">
                Section Availability
              </h2>

              <p className="mt-1 text-xs text-gray-500">
                Each row shows the required and available
                question capacity.
              </p>
            </div>

            <label className="inline-flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={showOnlyIssues}
                onChange={(event) =>
                  setShowOnlyIssues(
                    event.target.checked
                  )
                }
                className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />

              <span className="text-xs font-medium text-gray-600">
                Show only issues
              </span>
            </label>
          </div>

          {visibleRows.length > 0 ? (
            <BlueprintAvailabilityTable
              availability={
                visibleRows
              }
              rows={visibleRows}
              data={visibleRows}
              onCreateQuestions={
                onCreateQuestions
              }
            />
          ) : normalizedRows.length >
            0 ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-5 py-8 text-center">
              <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                <svg
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="h-5 w-5"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M16.704 5.29a1 1 0 010 1.42l-7.2 7.2a1 1 0 01-1.414 0l-3.2-3.2a1 1 0 011.414-1.42l2.493 2.494 6.493-6.494a1 1 0 011.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>

              <p className="mt-3 text-sm font-semibold text-emerald-900">
                All sections are available
              </p>

              <p className="mt-1 text-xs text-emerald-700">
                No availability issues were found.
              </p>
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-gray-300 bg-gray-50 px-5 py-10 text-center">
              <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-500">
                <svg
                  viewBox="0 0 20 20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  className="h-5 w-5"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M4 5.5A1.5 1.5 0 015.5 4h9A1.5 1.5 0 0116 5.5v9a1.5 1.5 0 01-1.5 1.5h-9A1.5 1.5 0 014 14.5v-9z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M7 8h6M7 11h4"
                  />
                </svg>
              </div>

              <p className="mt-3 text-sm font-medium text-gray-700">
                No availability data
              </p>

              <p className="mt-1 text-xs text-gray-500">
                The blueprint does not currently
                contain any availability results.
              </p>
            </div>
          )}

          {!isReady &&
            onCreateQuestions && (
              <div className="flex flex-col gap-3 rounded-xl border border-red-200 bg-red-50 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-semibold text-red-900">
                    Questions are missing
                  </p>

                  <p className="mt-1 text-xs leading-5 text-red-700">
                    Add or import the required English
                    questions before using this blueprint
                    to generate the quiz.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={onCreateQuestions}
                  className="shrink-0 rounded-lg bg-red-600 px-3 py-2 text-xs font-semibold text-white transition hover:bg-red-700"
                >
                  Add Questions
                </button>
              </div>
            )}
        </div>
      </div>
    </div>
  );
}